import {
  Box,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { KeyTermsSectionFragment } from '@client/graphql/__generated__/types';
import { ListChevronsDownUpIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import {
  formatKeyTermValue,
  getGroupedKeyTermsToDisplay,
  getParticipantInfo,
  KeyTermGroup,
  KeyTermValue,
} from '~/common/pages/Compliance/utils/ingestionDocuments.utils';
import { Participants } from './types';

interface KeyTermsPanelProps {
  keyTerms: KeyTermsSectionFragment[];
  /**
   * Transaction participants, rendered as a "People" tab — mirroring the shared
   * KeyTermsCard, where participants live alongside key terms rather than in a
   * separate card.
   */
  participants?: Participants | null;
}

/**
 * Maps a key term group title to a short tab label, mirroring the shared
 * `KeyTermsCard` behavior ("Purchase ..." -> "Terms", "... date ..." ->
 * "Dates"). Kept local so this lean read-only panel does not depend on the
 * heavy shared card.
 */
const formatKeyTermsTabLabel = (title?: string): string => {
  const normalizedTitle = title?.toLowerCase() ?? '';

  if (normalizedTitle.includes('purchase')) {
    return 'Terms';
  }

  if (normalizedTitle.includes('date')) {
    return 'Dates';
  }

  return title || 'Terms';
};

const keyTermsTabProps = {
  _focusVisible: { boxShadow: 'none' },
  _hover: { color: 'compliance.tab.selected.text' },
  _selected: {
    color: 'compliance.tab.selected.text',
    fontWeight: 'normal',
  },
  color: 'compliance.tab.text',
  flexShrink: 0,
  fontSize: 'md',
  fontWeight: 'normal',
  lineHeight: 1,
  px: 0,
  whiteSpace: 'nowrap' as const,
} as const;

const hiddenScrollbarStyles = {
  '&::-webkit-scrollbar': { display: 'none' },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
} as const;

/**
 * Renders the read-only label/value rows for a single key terms group. The
 * value is taken from the group's pre-formatted `value` (produced by
 * {@link getGroupedKeyTermsToDisplay}), falling back to {@link formatKeyTermValue}
 * for the raw field value. A value-sources tooltip surfaces the underlying
 * source labels when available, matching the shared card's read-only behavior.
 */
const KeyTermGroupBody: FC<{ group: KeyTermGroup }> = ({ group }) => (
  <VStack align="stretch" spacing={1}>
    {group.fields.map(({ label, value, field }, fieldIndex) => {
      const displayValue =
        value ?? formatKeyTermValue(field.value as KeyTermValue, field.key);

      const hasValuesSources = field.values && field.values.length > 0;
      const tooltipContent = hasValuesSources ? (
        <VStack align="stretch" spacing={1}>
          {field.values.map((valueObj, idx) => (
            <Flex
              key={idx}
              align="center"
              color={
                valueObj.isCurrent ? 'compliance.status.success' : undefined
              }
              gap={4}
              justify="space-between"
            >
              <Text fontSize="sm" textAlign="left">
                Source: {valueObj.sourceLabel || 'Unknown source'}
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {formatKeyTermValue(valueObj.value as KeyTermValue, field.key)}
              </Text>
            </Flex>
          ))}
        </VStack>
      ) : null;

      return (
        <Flex key={fieldIndex} align="center" justify="space-between">
          <Flex align="center" flex="1" gap={1} mr={2}>
            <Text
              color="compliance.text.muted"
              fontSize="md"
              fontWeight="normal"
            >
              {label}
            </Text>
          </Flex>
          <Tooltip
            bg="compliance.tooltip.bg"
            borderColor="compliance.tooltip.border"
            borderWidth="1px"
            color="compliance.tooltip.text"
            isDisabled={!hasValuesSources}
            label={tooltipContent}
            placement="top"
          >
            <Text
              color="compliance.text.primary"
              fontSize="md"
              fontWeight="normal"
              textAlign="right"
            >
              {displayValue}
            </Text>
          </Tooltip>
        </Flex>
      );
    })}
  </VStack>
);

/**
 * Renders the "People" tab body: the participant label/value rows produced by
 * the shared {@link getParticipantInfo} (e.g. "Buyer", "Listing Agent",
 * "Office"). Same read-only label/value layout as a key-terms group.
 */
const ParticipantInfoBody: FC<{
  rows: Array<{ label: string; value: string }>;
}> = ({ rows }) => (
  <VStack align="stretch" spacing={1}>
    {rows.map(({ label, value }, index) => (
      <Flex key={index} align="center" justify="space-between">
        <Text color="compliance.text.muted" fontSize="md" fontWeight="normal">
          {label}
        </Text>
        <Text
          color="compliance.text.primary"
          fontSize="md"
          fontWeight="normal"
          textAlign="right"
        >
          {value}
        </Text>
      </Flex>
    ))}
  </VStack>
);

/**
 * Lean, read-only key-terms section for the agent compliance review left rail.
 *
 * Mirrors the shared {@link KeyTermsCard}'s header pattern — the "Key terms"
 * title (collapsed) is replaced by the tab strip when expanded, with the same
 * expand/collapse toggle icon and tab/row styling — but deliberately omits the
 * redundant property-details section, download button, Darwin property sync,
 * and all editing affordances (none belong on the login-free public review page,
 * which already renders its own property summary). It reuses only the shared
 * tab/body DATA logic ({@link getGroupedKeyTermsToDisplay} +
 * {@link formatKeyTermValue}), not the heavy card chrome.
 */
export const KeyTermsPanel: FC<KeyTermsPanelProps> = ({
  keyTerms,
  participants,
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const [tabIndex, setTabIndex] = useState(0);

  const groups = useMemo(
    () => (keyTerms.length === 0 ? [] : getGroupedKeyTermsToDisplay(keyTerms)),
    [keyTerms]
  );

  // Participant rows for the "People" tab, via the same shared helper the
  // compliance KeyTermsCard uses.
  const participantInfo = useMemo(
    () =>
      getParticipantInfo(
        (participants ?? undefined) as Parameters<typeof getParticipantInfo>[0]
      ),
    [participants]
  );

  const hasPeople = participantInfo.length > 0;
  const hasContent =
    groups.some((group) => group.fields.length > 0) || hasPeople;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      overflow="hidden"
    >
      <Tabs
        index={tabIndex}
        size="md"
        variant="unstyled"
        onChange={setTabIndex}
      >
        <Flex align="center" gap={2} justify="space-between" px={4} py={3}>
          {/* Title when collapsed (or empty); tab strip when expanded — same
              swap the shared card does. */}
          <Flex align="center" flex="1" minW={0} overflow="hidden">
            {isOpen && hasContent ? (
              <TabList
                columnGap={3}
                flex="1"
                minW={0}
                overflowX="auto"
                overflowY="hidden"
                sx={hiddenScrollbarStyles}
              >
                {groups.map((group, index) => (
                  <Tab key={`${group.title}-${index}`} {...keyTermsTabProps}>
                    {formatKeyTermsTabLabel(group.title)}
                  </Tab>
                ))}
                {hasPeople && <Tab {...keyTermsTabProps}>People</Tab>}
              </TabList>
            ) : (
              <Text
                color="compliance.text.primary"
                flexShrink={0}
                fontSize="md"
                fontWeight="normal"
              >
                Key terms
              </Text>
            )}
          </Flex>

          <IconButton
            aria-label="toggle-key-terms"
            color="compliance.button.secondary.text"
            height="24px"
            icon={
              <Icon
                as={ListChevronsDownUpIcon}
                boxSize="16px"
                style={{
                  transform: isOpen ? 'none' : 'rotate(180deg)',
                  transition: 'transform 180ms ease',
                }}
              />
            }
            p={1}
            size="xs"
            variant="ghost"
            onClick={onToggle}
          />
        </Flex>

        <Collapse animateOpacity in={isOpen}>
          <Box pb={3} px={4}>
            {hasContent ? (
              <TabPanels>
                {groups.map((group, index) => (
                  <TabPanel key={`${group.title}-${index}`} p={0} pt={1}>
                    <KeyTermGroupBody group={group} />
                  </TabPanel>
                ))}
                {hasPeople && (
                  <TabPanel p={0} pt={1}>
                    <ParticipantInfoBody rows={participantInfo} />
                  </TabPanel>
                )}
              </TabPanels>
            ) : (
              <Text color="compliance.text.muted" fontSize="sm">
                No key terms
              </Text>
            )}
          </Box>
        </Collapse>
      </Tabs>
    </Box>
  );
};
